import { useState } from 'react'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { SegmentedControl } from '@/components/ui/segmented-control'
import type { AgentRunBucket, RunMetrics } from '@/models/agentRunReport'
import { SERIES_COLORS } from './chartPalette'
import { formatCost, formatCount, formatLatency, formatPercent, formatTokens } from './format'
import { formatPeriod } from './period'

interface Metric {
  value: string
  label: string
  /** null leaves a gap in the line — an unmeasured bucket is not a zero one. */
  pick: (bucket: RunMetrics) => number | null
  format: (value: number) => string
}

const METRICS: Metric[] = [
  {
    value: 'errorRate',
    label: 'Error rate',
    pick: (b) => (b.runs ? b.errorRate : null),
    format: (v) => formatPercent(v),
  },
  {
    value: 'latencyP95Ms',
    label: 'p95 latency',
    pick: (b) => b.latencyP95Ms,
    format: formatLatency,
  },
  { value: 'tokens', label: 'Tokens', pick: (b) => b.tokens, format: formatTokens },
  {
    value: 'costUsd',
    label: 'Cost',
    // A bucket with nothing priced has an unknown cost, not a cost of zero.
    pick: (b) => (b.pricedRuns ? b.costUsd : null),
    format: formatCost,
  },
  { value: 'runs', label: 'Runs', pick: (b) => b.runs, format: formatCount },
]

/** One metric over time on a single axis — never two scales in one frame. */
export function MetricTrendChart({
  buckets,
  granularity,
}: {
  buckets: AgentRunBucket[]
  granularity: string
}) {
  const [selected, setSelected] = useState(METRICS[0].value)
  const metric = METRICS.find((m) => m.value === selected) ?? METRICS[0]
  const data = buckets.map((bucket) => ({ period: bucket.period, value: metric.pick(bucket) }))

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h3 className="text-md font-semibold">{metric.label} over time</h3>
        <SegmentedControl
          value={selected}
          onValueChange={setSelected}
          options={METRICS.map((m) => ({ value: m.value, label: m.label }))}
          className="max-w-xl"
        />
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="#E4E3E0" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="period"
            tickFormatter={(value: string) => formatPeriod(value, granularity)}
            tick={{ fill: '#666764', fontSize: 12 }}
            stroke="#D6D5D2"
            minTickGap={24}
          />
          <YAxis
            tickFormatter={metric.format}
            tick={{ fill: '#666764', fontSize: 12 }}
            stroke="#D6D5D2"
            width={64}
          />
          <Tooltip
            labelFormatter={(value) => formatPeriod(String(value), granularity, true)}
            formatter={(_value, _name, item) => {
              // Read the datum, not the value: recharts types the latter as
              // non-null, so a gap would format as 0.
              const raw = (item as { payload?: { value: number | null } }).payload?.value
              return [raw == null ? 'no data' : metric.format(raw), metric.label]
            }}
            contentStyle={{ borderRadius: 8, borderColor: '#E4E3E0', fontSize: 12 }}
          />
          <Line
            type="linear"
            dataKey="value"
            name={metric.label}
            connectNulls={false}
            stroke={SERIES_COLORS[0]}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: '#ffffff' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
